import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { ReactNode, useMemo } from "react";
import { Control, useForm } from "react-hook-form";

import APAccordion from "../../../components/APAccordion";
import APTextField from "../../../components/form/APTextField";
import TermsButton from "../../../components/TermsButton";
import { toDollars } from "../../../helpers/currencies";
import { PersonaliseQuoteInputs } from "../../../helpers/quotes";
import VehicleOptionCard from "../../../quote/components/VehicleOptionCard";
import { QuoteFormatted } from "../../../types/Quote";
import {
  calculateWeeklyVehicleOptionCost,
  VehicleOption,
} from "../../../types/Vehicles";

type Inputs = {
  search: string;
};

export type VehicleOptionListVariant = "card" | "text";

const VehicleOptionsList = ({
  title,
  aboveOptionsContent,
  options,
  defaultExpanded = false,
  quote,
  control,
  filterable = false,
  forceOpen = false,
  forceClose = false,
  disableCheckboxes = false,
  editAction,
  variant = "card",
  emptyMessage = "No options found.",
  showTerms = false,
}: {
  title: string;
  aboveOptionsContent?: ReactNode;
  options: VehicleOption[];
  defaultExpanded?: boolean;
  quote?: QuoteFormatted;
  control?: Control<PersonaliseQuoteInputs>;
  filterable?: boolean;
  forceOpen?: boolean;
  forceClose?: boolean;
  disableCheckboxes?: boolean;
  editAction?: () => void;
  variant?: VehicleOptionListVariant;
  emptyMessage?: string;
  showTerms?: boolean;
}) => {
  const { control: optionsControl, watch } = useForm<Inputs>({
    mode: "onTouched",
  });
  const search = watch("search");
  const optionCards: ReactNode[] = [];

  const filteredOptions = useMemo(
    () =>
      filterable
        ? options.filter(
            (option) =>
              option.name &&
              option.name.toLowerCase().includes(search?.toLowerCase() || "")
          )
        : options,
    [options, filterable, search]
  );

  filteredOptions.sort((a, b) =>
    a.name && b.name ? a.name.localeCompare(b.name) : 0
  );

  filteredOptions.forEach((option) => {
    const renderedOption =
      variant === "card" ? (
        <VehicleOptionCard
          id={option.id}
          key={option.id}
          catchEOptionId={option.catchEOptionId}
          label={option.name}
          price={
            quote
              ? `${toDollars(
                  calculateWeeklyVehicleOptionCost(
                    option.price,
                    quote.savings.raw,
                    quote.term.raw
                  ),
                  0
                )}/wk^`
              : undefined
          }
          imageUrl={null}
          control={control}
          showImage={false}
          disableCheckbox={disableCheckboxes}
        />
      ) : (
        <Typography variant="bodyMedium" key={option.id}>
          {option.name}
        </Typography>
      );

    optionCards.push(renderedOption);
  });

  return (
    <APAccordion
      title={title}
      defaultExpanded={defaultExpanded}
      forceOpen={forceOpen}
      forceClose={forceClose}
      leftIcon={editAction ? <EditOutlinedIcon /> : undefined}
      handleLeftIconClick={editAction}
    >
      {filterable && (
        <Box my={2}>
          <APTextField<Inputs>
            name="search"
            label="Search"
            control={optionsControl}
            validations={{}}
            errors={{}}
            defaultValue={""}
          />
        </Box>
      )}
      <Stack spacing={1} sx={{ mt: 2 }}>
        {aboveOptionsContent && <Box mb={1}>{aboveOptionsContent}</Box>}
        {optionCards.length > 0 ? (
          optionCards
        ) : (
          <Typography variant="labelLarge">{emptyMessage}</Typography>
        )}
        {showTerms && <TermsButton termsId="vehicleOption" />}
      </Stack>
      {filteredOptions.length === 0 && filterable && (
        <Typography variant="bodyMedium">
          No options match your search
        </Typography>
      )}
    </APAccordion>
  );
};

export default VehicleOptionsList;
